// Copyright (c) Microsoft. All rights reserved.

import * as signalR from '@microsoft/signalr';
import { Constants } from '../../../Constants';
import { COPY } from '../../../assets/strings';
import { getFriendlyChatName } from '../../../libs/hooks/useChat';
import { AlertType } from '../../../libs/models/AlertType';
import { AuthorRoles, ChatMessageType, IChatMessage } from '../../../libs/models/ChatMessage';
import { IChatUser } from '../../../libs/models/ChatUser';
import { PlanState } from '../../../libs/models/Plan';
import { BackendServiceUrl } from '../../../libs/services/BaseService';
import { StoreMiddlewareAPI } from '../../app/store';
import { addAlert, setMaintenance } from '../app/appSlice';
import { ChatState } from '../conversations/ChatState';

/*
 * This is a module that encapsulates the SignalR connection
 * to the messageRelayHub on the server.
 */

// These have to match the callback names used in the backend
const enum SignalRCallbackMethods {
    ReceiveMessage = 'ReceiveMessage',
    ReceiveMessageUpdate = 'ReceiveMessageUpdate',
    UserJoined = 'UserJoined',
    ReceiveUserTypingState = 'ReceiveUserTypingState',
    ReceiveBotResponseStatus = 'ReceiveBotResponseStatus',
    GlobalDocumentUploaded = 'GlobalDocumentUploaded',
    ChatEdited = 'ChatEdited',
    ChatDeleted = 'ChatDeleted',
    GlobalSiteMaintenance = 'GlobalSiteMaintenance',
}

const RETRY_DELAY_IN_MS = 5000;

type HubAccessTokenProvider = () => Promise<string>;

// The hub requires an access token. The provider is set by the app once the auth libraries are available.
let hubAccessTokenProvider: HubAccessTokenProvider | undefined = undefined;
let isHubAccessTokenRequired = true;
// Token fetched while deciding whether to connect, handed over to the connection attempt that follows.
let prefetchedHubAccessToken = '';

export const setHubAccessTokenProvider = (provider: HubAccessTokenProvider, isTokenRequired = true) => {
    hubAccessTokenProvider = provider;
    isHubAccessTokenRequired = isTokenRequired;
};

const getHubAccessToken = async (): Promise<string> => {
    if (prefetchedHubAccessToken) {
        const token = prefetchedHubAccessToken;
        prefetchedHubAccessToken = '';
        return token;
    }
    if (hubAccessTokenProvider === undefined) {
        return '';
    }
    try {
        return await hubAccessTokenProvider();
    } catch (err) {
        console.error('SignalR token alma hatası: ', err);
        return '';
    }
};

// Set up a SignalR connection to the messageRelayHub on the server
const setupSignalRConnectionToChatHub = () => {
    const connectionHubUrl = new URL('/messageRelayHub', BackendServiceUrl);
    const signalRConnectionOptions = {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        logger: signalR.LogLevel.Warning,
        // Browsers cannot set headers on WebSockets; the client sends the token as the access_token query parameter.
        accessTokenFactory: getHubAccessToken,
    };

    // Create the connection instance
    // withAutomaticReconnect will automatically try to reconnect and generate a new socket connection if needed
    const hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(connectionHubUrl.toString(), signalRConnectionOptions)
        .withAutomaticReconnect()
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.Information)
        .build();

    // Note: to keep the connection open the serverTimeout should be
    // larger than the KeepAlive value that is set on the server
    // keepAliveIntervalInMilliseconds default is 15000 and we are using default
    // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
    hubConnection.serverTimeoutInMilliseconds = 60000;

    return hubConnection;
};

const joinAllConversationGroups = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    const conversationIds = Object.keys(store.getState().conversations.conversations);
    conversationIds.forEach((id) => {
        hubConnection
            .invoke('AddClientToGroupAsync', id)
            .catch((err) => {
                console.error(`SignalR grup katılım hatası (${id}):`, err);
            });
    });
};

const registerCommonSignalConnectionEvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    // Re-establish the connection if connection dropped
    hubConnection.onclose((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
            const errorMessage = 'Bağlantı kapandı. Lütfen sayfayı yenileyerek bağlantıyı yeniden bağlayın.';
            store.dispatch(
                addAlert({
                    message: String(errorMessage),
                    type: AlertType.Error,
                    id: Constants.app.CONNECTION_ALERT_ID,
                }),
            );
            console.log(errorMessage, error);
        }
    });

    hubConnection.onreconnecting((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Reconnecting) {
            const errorMessage = 'Bağlantı kayboldu. Yeniden bağlanılıyor...';
            store.dispatch(
                addAlert({
                    message: String(errorMessage),
                    type: AlertType.Info,
                    id: Constants.app.CONNECTION_ALERT_ID,
                }),
            );
            console.log(errorMessage, error);
        }
    });

    hubConnection.onreconnected((connectionId = '') => {
        if (hubConnection.state === signalR.HubConnectionState.Connected) {
            // Yeni bağlantı ID'si atandığında grupları tekrar katıl
            joinAllConversationGroups(hubConnection, store);
            const message = 'Bağlantı yeniden kuruldu.';
            store.dispatch(addAlert({ message, type: AlertType.Success, id: Constants.app.CONNECTION_ALERT_ID }));
            console.log(message + ` Bağlantı id: ${connectionId}`);
        }
    });
};

const startSignalRConnection = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    const retryLater = () => {
        setTimeout(() => {
            startSignalRConnection(hubConnection, store);
        }, RETRY_DELAY_IN_MS);
    };

    void getHubAccessToken().then((token) => {
        // Without a token the server rejects the connection, so do not even try.
        if (isHubAccessTokenRequired && !token) {
            retryLater();
            return;
        }
        prefetchedHubAccessToken = token;

        hubConnection
            .start()
            .then(() => {
                console.assert(hubConnection.state === signalR.HubConnectionState.Connected);
                console.log('SignalR bağlantısı kuruldu');
                // Bağlantı kurulduktan sonra store'daki tüm conversation gruplarına katıl
                joinAllConversationGroups(hubConnection, store);
            })
            .catch((err) => {
                console.assert(hubConnection.state === signalR.HubConnectionState.Disconnected);
                console.error('SignalR bağlantı hatası: ', err);
                retryLater();
            })
            .finally(() => {
                prefetchedHubAccessToken = '';
            });
    });
};

const registerSignalREvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    // The server sends the events of all chats of the user to each of the user's connections.
    // Events of chats that are not loaded in this tab are ignored.
    const isKnownChat = (chatId: string) => chatId in store.getState().conversations.conversations;

    hubConnection.on(
        SignalRCallbackMethods.ReceiveMessage,
        (chatId: string, senderId: string, message: IChatMessage) => {
            if (!isKnownChat(chatId)) return;

            if (message.authorRole === AuthorRoles.Bot) {
                const loggedInUserId = store.getState().app.activeUserInfo?.id;
                const responseToLoggedInUser = loggedInUserId === senderId;
                message.planState =
                    message.type === ChatMessageType.Plan && responseToLoggedInUser
                        ? PlanState.PlanApprovalRequired
                        : PlanState.Disabled;
            }

            store.dispatch({ type: 'conversations/addMessageToConversationFromServer', payload: { chatId, message } });
        },
    );

    hubConnection.on(SignalRCallbackMethods.ReceiveMessageUpdate, (message: IChatMessage) => {
        const { chatId, id: messageId, content } = message;
        if (!isKnownChat(chatId)) return;

        // If tokenUsage is defined, that means full message content has already been streamed and updated from server. No need to update content again.
        store.dispatch({
            type: 'conversations/updateMessageProperty',
            payload: {
                chatId,
                messageIdOrIndex: messageId,
                property: message.tokenUsage ? 'tokenUsage' : 'content',
                value: message.tokenUsage ?? content,
                frontLoad: true,
            },
        });
    });

    hubConnection.on(SignalRCallbackMethods.UserJoined, (chatId: string, userId: string) => {
        if (!isKnownChat(chatId)) return;

        const user: IChatUser = {
            id: userId,
            online: false,
            fullName: '',
            emailAddress: '',
            isTyping: false,
            photo: '',
        };
        store.dispatch({ type: 'conversations/addUserToConversation', payload: { user, chatId } });
    });

    hubConnection.on(
        SignalRCallbackMethods.ReceiveUserTypingState,
        (chatId: string, userId: string, isTyping: boolean) => {
            if (!isKnownChat(chatId)) return;

            store.dispatch({
                type: 'conversations/updateUserIsTypingFromServer',
                payload: { chatId, userId, isTyping },
            });
        },
    );

    hubConnection.on(SignalRCallbackMethods.ReceiveBotResponseStatus, (chatId: string, status: string) => {
        if (!isKnownChat(chatId)) return;

        store.dispatch({ type: 'conversations/updateBotResponseStatus', payload: { chatId, status } });
    });

    hubConnection.on(SignalRCallbackMethods.GlobalDocumentUploaded, (fileNames: string, userName: string) => {
        store.dispatch(
            addAlert({ message: `${userName} tüm sohbetlere ${fileNames} dosyası yüklendi`, type: AlertType.Info }),
        );
    });

    hubConnection.on(SignalRCallbackMethods.ChatEdited, (chat: ChatState) => {
        const { id, title } = chat;
        if (!isKnownChat(id)) return;

        store.dispatch({ type: 'conversations/editConversationTitle', payload: { id, newTitle: title } });
    });

    // User Id is that of the user who initiated the deletion.
    hubConnection.on(SignalRCallbackMethods.ChatDeleted, (chatId: string, userId: string) => {
        if (!isKnownChat(chatId)) return;

        const conversations = store.getState().conversations.conversations;
        const friendlyChatName = getFriendlyChatName(conversations[chatId]);
        const deletedByAnotherUser = userId !== store.getState().app.activeUserInfo?.id;

        store.dispatch(
            addAlert({
                message: deletedByAnotherUser
                    ? COPY.CHAT_DELETED_MESSAGE(friendlyChatName)
                    : `Sohbet {${friendlyChatName}} başarıyla silindi.`,
                type: AlertType.Warning,
            }),
        );

        if (deletedByAnotherUser)
            store.dispatch({
                type: 'conversations/disableConversation',
                payload: chatId,
            });
    });

    hubConnection.on(SignalRCallbackMethods.GlobalSiteMaintenance, () => {
        store.dispatch(setMaintenance(true));
    });
};

// This is a singleton instance of the SignalR connection
let hubConnection: signalR.HubConnection | undefined = undefined;

// This function will return the singleton instance of the SignalR connection,
// or undefined when the connection has not been started yet.
export const getHubConnection = () => hubConnection;

// Creates and starts the SignalR connection. Safe to call more than once.
// Must only be called once an access token can be acquired, since the hub requires authentication.
export const ensureHubConnectionStarted = (store: StoreMiddlewareAPI) => {
    if (hubConnection !== undefined) {
        return;
    }
    hubConnection = setupSignalRConnectionToChatHub();
    registerCommonSignalConnectionEvents(hubConnection, store);
    registerSignalREvents(hubConnection, store);

    // Start the signalR connection to make sure messages are
    // sent to all clients and received by all clients
    startSignalRConnection(hubConnection, store);
};
